import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Modal, Input, PillButton } from '../ui';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as EraserIcon } from '../assets/icons/eraser.svg';
import { ReactComponent as UserMinusIcon } from '../assets/icons/user-minus.svg';

// Qué se borra y qué se conserva en cada acción (POST /api/account/wipe-data
// y POST /api/account/delete). Las claves van enteras, no construidas, para
// que esta lista se compare a simple vista con server/SECURITY-ROUTES.md.
const ACTIONS = {
  wipe: {
    phrase: 'WIPE',
    endpoint: '/account/wipe-data',
    icon: EraserIcon,
    title: 'account.danger.wipeTitle',
    submit: 'account.danger.wipeSubmit',
    deletes: ['account.danger.wipeDeletes1', 'account.danger.wipeDeletes2', 'account.danger.wipeDeletes3', 'account.danger.wipeDeletes4'],
    keeps: ['account.danger.wipeKeeps1', 'account.danger.wipeKeeps2', 'account.danger.wipeKeeps3', 'account.danger.wipeKeeps4'],
  },
  delete: {
    phrase: 'DELETE',
    endpoint: '/account/delete',
    icon: UserMinusIcon,
    title: 'account.danger.deleteTitle',
    submit: 'account.danger.deleteSubmit',
    deletes: ['account.danger.deleteDeletes1', 'account.danger.deleteDeletes2', 'account.danger.deleteDeletes3', 'account.danger.deleteDeletes4', 'account.danger.deleteDeletes5'],
    keeps: ['account.danger.deleteKeeps1'],
  },
};

/**
 * Modal de confirmación de "Vaciar datos" y "Eliminar cuenta".
 * action: 'wipe' | 'delete' | null (cerrado). onDone(action, data) solo se
 * llama si el servidor ha ejecutado la operación.
 */
function DangerActionModal({ action, onClose, onDone }) {
  const { t } = useTranslation();
  const cfg = ACTIONS[action];

  const [security, setSecurity] = useState(null); // { hasPin, hasMasterPassword }
  const [secret, setSecret] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState(null); // { text, setup? }
  const [loading, setLoading] = useState(false);

  // Cada apertura empieza limpia y consulta con qué factor cuenta la cuenta.
  useEffect(() => {
    if (!action) return undefined;
    setSecret(''); setConfirmation(''); setError(null); setLoading(false); setSecurity(null);
    let cancelled = false;
    api.get('/account/security')
      .then((res) => { if (!cancelled) setSecurity(res.data); })
      .catch(() => { if (!cancelled) setSecurity({}); });
    return () => { cancelled = true; };
  }, [action]);

  const describeError = useCallback((err) => {
    const data = err.response?.data || {};
    switch (data.code) {
      case 'second_factor_invalid':
        return { text: t('account.danger.errInvalid', { remaining: data.remaining ?? 0 }) };
      case 'too_many_attempts':
        return { text: t('account.danger.errTooMany', { minutes: Math.max(1, Math.ceil((data.retryAfter || 60) / 60)) }) };
      case 'second_factor_setup_needed':
        return { text: t('account.danger.errSetup'), setup: true };
      case 'owns_organizations':
        return { text: t('account.danger.errOwnsOrgs', { names: (data.orgs || []).map((o) => o.name).join(', ') }) };
      case 'confirmation_invalid':
        return { text: t('account.danger.errConfirm') };
      default:
        return { text: t('account.danger.errGeneric') };
    }
  }, [t]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!cfg || loading) return;
    setLoading(true);
    setError(null);
    // Un solo campo: se manda como PIN y/o contraseña maestra según los que
    // tenga la cuenta; el servidor prueba cada uno con el factor que exista.
    const body = { confirmation };
    if (security?.hasPin) body.pin = secret;
    if (security?.hasMasterPassword) body.masterPassword = secret;
    try {
      const res = await api.post(cfg.endpoint, body);
      onDone?.(action, res.data);
    } catch (err) {
      setError(describeError(err));
      setSecret('');
      setLoading(false);
    }
  }, [cfg, loading, confirmation, security, secret, action, onDone, describeError]);

  if (!cfg) return null;

  const noFactor = !!security && !security.hasPin && !security.hasMasterPassword;
  const canSubmit = !!security && !noFactor && secret !== '' && confirmation === cfg.phrase && !loading;

  return (
    <Modal open={!!action} onClose={loading ? undefined : onClose} title={t(cfg.title)}>
      <form className="sgc-account__form" onSubmit={handleSubmit}>
        <div className="sgc-account__danger-list">
          <h3 className="sgc-account__danger-heading sgc-account__danger-heading--delete">{t('account.danger.deletesHeading')}</h3>
          <ul>{cfg.deletes.map((key) => <li key={key}>{t(key)}</li>)}</ul>
          <h3 className="sgc-account__danger-heading">{t('account.danger.keepsHeading')}</h3>
          <ul>{cfg.keeps.map((key) => <li key={key}>{t(key)}</li>)}</ul>
        </div>
        <p className="sgc-account__merge-warning">{t('account.danger.irreversible')}</p>

        {noFactor ? (
          <p className="sgc-account__form-error">
            {t('account.danger.errSetup')}{' '}
            <Link to="/account/security" onClick={onClose}>{t('account.danger.setPinLink')}</Link>
          </p>
        ) : (
          <>
            <Input
              icon={KeyIcon}
              label={t('account.danger.secretLabel')}
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="off"
              disabled={!security}
            />
            <Input
              icon={AlertTriangleIcon}
              label={t('account.danger.confirmLabel', { phrase: cfg.phrase })}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
          </>
        )}

        {error && (
          <p className="sgc-account__form-error" role="alert">
            {error.text}{' '}
            {error.setup && <Link to="/account/security" onClick={onClose}>{t('account.danger.setPinLink')}</Link>}
          </p>
        )}

        <PillButton type="submit" variant="danger" icon={cfg.icon} disabled={!canSubmit} loading={loading}>
          {t(cfg.submit)}
        </PillButton>
      </form>
    </Modal>
  );
}

export default DangerActionModal;
